'use client'

import { ChevronDownIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { useId, useState } from 'react'

import { machuTheme } from '../machuTheme'

const FAQ_ITEMS: { question: string; answer: string }[] = [
  {
    question: 'Who can attend The Retreat?',
    answer:
      'The Retreat is open to magicians of all levels, from enthusiastic hobbyists to seasoned professionals. At the same time, the experience is thoughtfully designed for spouses and partners as well, so many attendees bring a guest who enjoys travel, culture, and shared experiences. The programme is designed so that both magicians and their partners can fully enjoy the journey together—from exploring Machu Picchu and the Sacred Valley during the day to sharing in the evening performances and gatherings.',
  },
  {
    question: 'Is the Retreat suitable for solo travellers?',
    answer:
      'Yes. Many guests attend The Retreat on their own and quickly become part of the family. Because the experience is intentionally small and social, solo travellers often find it one of the easiest ways to connect with like-minded people from around the world.',
  },
  {
    question: 'What airport should I fly into?',
    answer:
      'The Retreat begins in Cusco, Peru. Most international travellers will fly via Lima, connecting onward to Cusco. Detailed travel guidance will be provided to all guests once bookings are confirmed. Guests joining the Amazon expedition will begin their journey in Iquitos (also accessible via Lima) before flying to Cusco to join the main Retreat.',
  },
  {
    question: 'What should I know about acclimating whilst in Peru?',
    answer:
      'Cusco sits at a higher altitude, which is why The Retreat specifically begins in the Sacred Valley at a lower elevation. This gives guests time to acclimatise comfortably before our visit to Machu Picchu and the final night in Cusco.',
  },
  {
    question: 'How active is The Retreat?',
    answer:
      'The itinerary includes guided explorations of historic sites such as Machu Picchu and the Sacred Valley. Guests are always free to enjoy the surroundings in their own way. The journey has been thoughtfully designed so that everyone can fully embrace the experience, whether that means exploring every corner of the places we visit or simply soaking in the extraordinary landscapes and atmosphere.',
  },
  {
    question: 'Will there be magic performances and sessions?',
    answer:
      "Yes. The Retreat includes a number of one-of-a-kind magic sessions and performances throughout the experience. And the whole time, you'll be traveling and dining with the performers and the Vanishing Inc. team (including cofounders Andi Gladwin and Joshua Jay).",
  },
  {
    question: 'What happens after I book?',
    answer:
      'Once your place is confirmed, you will receive detailed travel guidance, packing suggestions, and itinerary information to help you prepare for the journey. Our team will also be available to assist with any questions leading up to The Retreat, along with access to our special attendee Facebook group.',
  },
  {
    question: 'Is travel insurance required?',
    answer:
      'Yes. Because this is an international travel experience, all guests are required to obtain comprehensive travel insurance covering trip cancellation, medical expenses, and travel disruptions.',
  },
  {
    question: 'What is the booking policy?',
    answer:
      'As The Retreats consistently sell out—and to ensure the highest quality experience, we commit to bookings, reservations and service providers well in advance. As a result, registrations are non-refundable, and travel insurance is a prerequisite. You can choose to pay in full or in five easy installments during the registration process. Where possible, we will assist in transferring your registration to another participant on our waiting list once The Retreat is fully subscribed. Please ensure you have sufficient medical, cancellation, and travel coverage for your trip.',
  },
]

export const FAQ = () => {
  const baseId = useId()
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <div className="max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6">
      <div className="py-6 md:py-14">
        <section aria-labelledby={`${baseId}-faq-heading`}>
          <h2
            id={`${baseId}-faq-heading`}
            className={`font-machu font-bold text-3xl ${machuTheme.accentText} border-b border-gray-400 pb-1 mb-6 text-center`}
          >
            Frequently Asked Questions
          </h2>

          <div className="lg:w-2/3 lg:mx-auto border-t border-stone-300/90">
            {FAQ_ITEMS.map((item, index) => {
              const triggerId = `${baseId}-faq-trigger-${index}`
              const panelId = `${baseId}-faq-panel-${index}`
              const isOpen = openIndex === index

              return (
                <div
                  key={item.question}
                  className="border-b border-stone-300/90"
                >
                  <h3 className="m-0 font-machu text-lg font-semibold md:text-xl">
                    <button
                      id={triggerId}
                      type="button"
                      onClick={() =>
                        setOpenIndex(prev => (prev === index ? null : index))
                      }
                      className={clsx(
                        'flex w-full cursor-pointer items-center justify-between gap-3 py-4 text-left',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                        'focus-visible:ring-[#b94d3c]'
                      )}
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                    >
                      <span className="pr-2 text-gray-900">{item.question}</span>
                      <ChevronDownIcon
                        className={clsx(
                          'h-6 w-6 shrink-0 transition-transform duration-200',
                          machuTheme.accentText,
                          isOpen && 'rotate-180'
                        )}
                        aria-hidden
                      />
                    </button>
                  </h3>
                  {isOpen && (
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={triggerId}
                      className="border-t border-stone-200/90 bg-stone-50/70 pb-5 pt-4 text-lg leading-relaxed text-gray-700"
                    >
                      {item.answer}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      </div>
    </div>
  )
}
